export function normalizeVersionCode(value, fallback = "single") {
	const normalized = String(value ?? "").trim();
	return normalized || fallback;
}

export function compareVersionCodesForDisplay(a, b) {
	const av = normalizeVersionCode(a, "single");
	const bv = normalizeVersionCode(b, "single");
	const an = Number.parseInt(av, 10);
	const bn = Number.parseInt(bv, 10);
	const aIsNumeric = Number.isInteger(an) && String(an) === av;
	const bIsNumeric = Number.isInteger(bn) && String(bn) === bv;
	if (aIsNumeric && bIsNumeric) return an - bn;
	if (aIsNumeric) return -1;
	if (bIsNumeric) return 1;
	return av.localeCompare(bv);
}

export function normalizeVersionList(versions = []) {
	return Array.from(
		new Set((versions || []).map((value) => normalizeVersionCode(value, "")).filter(Boolean)),
	);
}

function toExactVersionMatchToken(value) {
	return String(value ?? "").toUpperCase();
}

function toTrimmedVersionMatchToken(value) {
	return normalizeVersionCode(value, "").toUpperCase();
}

function parseVersionInteger(value) {
	const trimmed = normalizeVersionCode(value, "");
	if (!/^[+-]?\d+$/.test(trimmed)) return null;
	const parsed = Number.parseInt(trimmed, 10);
	return Number.isSafeInteger(parsed) ? parsed : null;
}

function resolveVersionMatchAmbiguity(candidates = [], preferredVersion = null) {
	if (candidates.length === 0) return null;
	if (preferredVersion) {
		const preferredToken = toTrimmedVersionMatchToken(preferredVersion);
		const preferredCandidate = candidates.find(
			(version) => toTrimmedVersionMatchToken(version) === preferredToken,
		);
		if (preferredCandidate) return preferredCandidate;
	}
	return candidates.length === 1 ? candidates[0] : null;
}

export function findExactVersionMatch(versions = [], value) {
	const target = toExactVersionMatchToken(value);
	if (!target) return null;
	return resolveVersionMatchAmbiguity(
		(versions || []).filter((version) => toExactVersionMatchToken(version) === target),
	);
}

function findTrimmedVersionMatch(versions = [], value, { preferredVersion = null } = {}) {
	const target = toTrimmedVersionMatchToken(value);
	if (!target) return null;
	return resolveVersionMatchAmbiguity(
		(versions || []).filter((version) => toTrimmedVersionMatchToken(version) === target),
		preferredVersion,
	);
}

function findIntegerVersionMatch(versions = [], value, { preferredVersion = null } = {}) {
	const target = parseVersionInteger(value);
	if (!Number.isSafeInteger(target)) return null;
	return resolveVersionMatchAmbiguity(
		(versions || []).filter((version) => parseVersionInteger(version) === target),
		preferredVersion,
	);
}

function findLoosePartialVersionMatch(versions = [], value, { preferredVersion = null } = {}) {
	const rawToken = toTrimmedVersionMatchToken(value);
	if (!rawToken) return null;

	const preferredToken = preferredVersion ? toTrimmedVersionMatchToken(preferredVersion) : "";
	const candidates = (versions || [])
		.map((version) => {
			const versionToken = toTrimmedVersionMatchToken(version);
			return { version, versionToken };
		})
		.filter(
			({ versionToken }) =>
				versionToken &&
				(versionToken.startsWith(rawToken) || rawToken.startsWith(versionToken)),
		);

	if (candidates.length === 0) return null;

	if (preferredToken) {
		const preferredCandidate = candidates.find(
			(candidate) => candidate.versionToken === preferredToken,
		);
		if (preferredCandidate) return preferredCandidate.version;
	}

	const ranked = candidates
		.map((candidate) => {
			const versionStartsWithRaw = candidate.versionToken.startsWith(rawToken);
			const rawStartsWithVersion = rawToken.startsWith(candidate.versionToken);
			return {
				...candidate,
				relationRank:
					versionStartsWithRaw && !rawStartsWithVersion
						? 0
						: rawStartsWithVersion && !versionStartsWithRaw
							? 1
							: 2,
				lengthDiff: Math.abs(candidate.versionToken.length - rawToken.length),
			};
		})
		.sort(
			(a, b) =>
				a.relationRank - b.relationRank ||
				a.lengthDiff - b.lengthDiff ||
				b.versionToken.length - a.versionToken.length ||
				compareVersionCodesForDisplay(a.version, b.version),
		);

	const best = ranked[0];
	const ambiguous = ranked.some(
		(candidate, index) =>
			index > 0 &&
			candidate.relationRank === best.relationRank &&
			candidate.lengthDiff === best.lengthDiff &&
			candidate.versionToken.length === best.versionToken.length,
	);
	return ambiguous ? null : best.version;
}

function resolveVersionMatch(value, versions = [], { preferredVersion = null } = {}) {
	const exactMatch = findExactVersionMatch(versions, value);
	if (exactMatch) {
		return { version: exactMatch, strategy: "exact" };
	}

	const trimmedMatch = findTrimmedVersionMatch(versions, value, { preferredVersion });
	if (trimmedMatch) {
		return { version: trimmedMatch, strategy: "trimmed" };
	}

	const integerMatch = findIntegerVersionMatch(versions, value, { preferredVersion });
	if (integerMatch) {
		return { version: integerMatch, strategy: "integer" };
	}

	const partialMatch = findLoosePartialVersionMatch(versions, value, { preferredVersion });
	if (partialMatch) {
		return { version: partialMatch, strategy: "partial" };
	}

	return { version: null, strategy: null };
}

function resolveDisplayVersionMatch(value, versions = []) {
	const exactMatch = findExactVersionMatch(versions, value);
	if (exactMatch) return exactMatch;

	const trimmedMatch = findTrimmedVersionMatch(versions, value);
	if (trimmedMatch) return trimmedMatch;

	const integerMatch = findIntegerVersionMatch(versions, value);
	if (integerMatch) return integerMatch;

	return null;
}

export function resolveDisplayVersionCode(value, knownVersions = [], fallback = "") {
	const versions = normalizeVersionList(knownVersions);
	const matchedVersion = resolveDisplayVersionMatch(value, versions);
	return matchedVersion || normalizeVersionCode(value, fallback);
}

export function findPartialVersionMatch(rawValue, versions = [], { assignedVersion = null } = {}) {
	if (findExactVersionMatch(versions, rawValue)) return null;
	if (findTrimmedVersionMatch(versions, rawValue, { preferredVersion: assignedVersion })) return null;
	if (findIntegerVersionMatch(versions, rawValue, { preferredVersion: assignedVersion })) return null;
	return findLoosePartialVersionMatch(versions, rawValue, {
		preferredVersion: assignedVersion,
	});
}

function getStoredRawVersion(student) {
	if (student?.raw_version !== undefined && student?.raw_version !== null) {
		return String(student.raw_version);
	}
	if (student?.original_special_code !== undefined && student?.original_special_code !== null) {
		return String(student.original_special_code);
	}
	return "";
}

function getVersionMatchSource(student, storedVersionGuessed) {
	const rawVersion = getStoredRawVersion(student);
	if (rawVersion.trim()) return rawVersion;
	if (storedVersionGuessed) return "";
	return String(student?.version ?? student?.special_code ?? "");
}

export function resolveStudentVersion(student, knownVersions = [], fallback = "single") {
	const versions = normalizeVersionList(knownVersions);
	const fallbackVersion = normalizeVersionCode(versions[0], fallback);
	const storedVersionGuessed =
		student?.version_guessed === true ||
		String(student?.version_guessed ?? "")
			.trim()
			.toUpperCase() === "Y";
	const rawVersion = getStoredRawVersion(student);
	const matchSource = getVersionMatchSource(student, storedVersionGuessed);
	const hasEnteredVersion = matchSource.trim().length > 0;
	const assignedRaw = normalizeVersionCode(student?.version ?? student?.special_code, fallbackVersion);
	const assignedVersion =
		resolveVersionMatch(assignedRaw, versions, { preferredVersion: fallbackVersion }).version ||
		fallbackVersion;
	const rawMatch = hasEnteredVersion
		? resolveVersionMatch(matchSource, versions, { preferredVersion: assignedVersion })
		: { version: null, strategy: null };
	const exactRawMatch = rawMatch.strategy === "exact" ? rawMatch.version : null;
	const trimmedRawMatch = rawMatch.strategy === "trimmed" ? rawMatch.version : null;
	const integerRawMatch = rawMatch.strategy === "integer" ? rawMatch.version : null;
	const prefixRawMatch = rawMatch.strategy === "partial" ? rawMatch.version : null;
	const partialRawMatch = trimmedRawMatch || integerRawMatch || prefixRawMatch;
	const resolvedVersion = rawMatch.version || assignedVersion;
	const versionGuessed = !rawMatch.version;
	const hasCodeMismatch = versionGuessed || rawMatch.strategy === "partial";

	return {
		rawVersion,
		assignedVersion,
		resolvedVersion,
		versionGuessed,
		exactRawMatch,
		trimmedRawMatch,
		integerRawMatch,
		prefixRawMatch,
		partialRawMatch,
		hasCodeMismatch,
	};
}
